import { makeAutoObservable, runInAction } from 'mobx'
import { map, each, isArray, groupBy, flatten, some, isEmpty } from '@dt/fe-utils/dist/lodash'
import { restaurantConfig, storeMobx, deskMobx } from '..'
import { ddayjs, store } from '@dt/fe-utils'
import OrderAPI from 'api/order.api'
import DeskAPI from 'api/desk.api'

interface IFilterOrder {
  status: any[]; // 应该是string[] 快速解决问题修改成null
  channel: string;
  eatType: string;
  sortName: string;
  sortType: string;
  sort: 'desc' | 'asc';
}

export enum TOrderFilterSecTab {
  all = 'all',
  // 堂食
  dinein = 'dinein',
  // 打包
  togo = 'togo',
  // 外卖
  delivery = 'delivery',
  // 自提
  pickup = 'pickup',
  // 车道自提
  carside = 'carside'
}

export enum TOrderFilterSourceType {
  // 堂食-POS
  pos = 'pos',
  // 堂食-桌码点餐
  scanQRcode = 'scanQRcode',
  // 堂食-线上点餐
  onlineMobileWebH5 = 'onlineMobileWebH5'
}

export enum TOrderStyle {
  OrderOne = 'order-first-style',
  orderTwo = 'order-second-style'
}
interface ISearchOrderOptions {
  search: '';
  type: string | undefined;
  day: string[];
  pageNo: number;
  last: boolean;
}

interface IStoreTotalSum {
  parentTime: number;
  list: {
    open: number;
    paid: number;
    voided: number;
    timeout: number;
    all: number;
    dinein?: number;
    dineinPos?: number;
    togo?: number;
    togoPos?: number;
    delivery?: number;
    deliveryPos?: number;
  } | any;
}

const OrderStatusMaps = {
  open: ['Pending', 'Paying', 'NotAccepted'],
  paid: 'Success',
  voided: 'Cancel',
  timeout: 'Timeout'
}

/**
 * 主要负责门店订单列表
 */
class OrderList {
  constructor () {
    makeAutoObservable(this)
  }

  isLoading = false

  orderStyle: TOrderStyle = store.get('order.list.style') || TOrderStyle.OrderOne;

  isError = false

  pageSize = 300

  orders: IOrder[] = []

  searchOrder: IOrder[] = []

  reservationOrder: IOrder[] = []

  isCanCallMeal: boolean = false

  kdsPara: any = {}

  /** 订单列表页查询条件 */
  doFilterOrder: IFilterOrder = {
    status: ['open'],
    channel: 'all',
    eatType: 'all',
    sortName: 'addTime',
    sortType: '1',
    sort: 'desc',
  }

  /** 订单搜索条件 */
  doSearchOrder: any = {
    search: '',
    type: 'nono',
    pageNo: 0,
    last: false,
    day: [ddayjs().subtract(40, 'days').startOf('day').format(), ddayjs().endOf('day').format()]
  }

  /** 手机点餐订单列表分页信息 */
  MobileOrderPageInfo = {
    pageNo: 0,
    isEnd: false
  }

  /** pos订单列表分页信息 */
  OrderpageInfo: { status: 'today' | 'yestoday'; today: { pageNo: number; isEnd: boolean }; yestoday: { pageNo: number; isEnd: boolean } } = {
    status: 'today',
    today: {
      pageNo: 0,
      isEnd: false
    },
    yestoday: {
      pageNo: 0,
      isEnd: false
    }
  }

  /** pos 订单列表其他分页搜索时间段 */
  extraQueryTime?: { startAcceptTime?: string; endAcceptTime?: string }

  /** 当前门店区间内订单的所有的分类汇总 */
  storeTotalSum: IStoreTotalSum = {
    parentTime: 0,
    list: {
      open: 0,
      paid: 0,
      voided: 0,
      timeout: 0,
      all: 0,
    }
  }

  /** 返回当前门店正在进行中的订单 */
  storeOrdings: IOrder[] = []

  /** 是否展示昨天的订单列表 */
  showYesterDayInfo = false

  /** 订单搜索选中类型 */
  selectedSearchType: string = 'all'

  /** 订单搜索选中搜索项 */
  selectedOptions: string[] = []

  /** 统计在订单中出现的外卖平台 */
  devliverCountMap: { [key: string]: number } = {}

  /** 第三方外卖方式 */
  thirdDeliveryWay: { [key: string]: any } = {}

  /** 用于判断一个订单是否已经出现过 */
  deliveryIdCache: { [key: number]: any } = {}

  needTableList = []

  private _state = 'meal'

  get state () {
    return this._state
  }

  set state (val: string) {
    this._state = val
  }

  /** 获取当前订单样式 */
  get getOrderStyle (): TOrderStyle {
    return this.orderStyle
  }

  get ordersByDate () {
    const sortOrders = this.orders.slice().filter(order => order.nono)
    const res: any = groupBy(sortOrders, (order: IOrder) => ddayjs(order.acceptTime ? order.acceptTime : order.addTime, 'client').format('YYYY-MM-DD'))
    const { startAcceptTime, endAcceptTime } = this.extraQueryTime || {}
    const filterExtraQueryTime = startAcceptTime && sortOrders.filter(item => ddayjs(item.acceptTime ? item.acceptTime : item.addTime).isBetween(ddayjs(startAcceptTime), ddayjs(endAcceptTime)))
    const result = {
      today: res[ddayjs().format('YYYY-MM-DD')],
      yestoday: (startAcceptTime) ? filterExtraQueryTime : res[ddayjs().subtract(1, 'days').format('YYYY-MM-DD')],
    }
    return result
  }

  get pickupThirdDeliveryWay () {
    return Object.keys(this.storeTotalSum.list).filter(key => key?.includes('pickup_')).map(i => i.replace('pickup_', ''))
  }

  get transformFilterOrder () {
    const result: any = {}
    each(this.doFilterOrder, (value: any, key) => {
      if (key === 'channel') {
        // 堂食或打包
        if (Array.isArray(value)) {
          result.channel = [...value]
        } else if (typeof value === 'object') {
          // 外卖
          const sourceType = value.sourceType || []
          if (sourceType.length > 0 && window.bo === 'cn') {
            result.channel = sourceType
          } else {
            result.sourceType = sourceType
          }
        } else {
          if (value !== 'all') {
            result.channel = [value]
          }
        }
      } else if (key === 'eatType') {
        if (value !== 'all') {
          result[key] = Array.isArray(value) ? [...value] : [value]
        }
      } else if (key === 'status') {
        result[key] = isArray(value) ? flatten(map(value, (item: 'open' | 'paid' | 'voided' | 'timeout') => OrderStatusMaps[item])) : [(OrderStatusMaps as any)[value]]
      } else if (key === 'all') {
        // 对应全部订单来源搜素项时，去掉可能存在的冗余查询条件
      } else {
        if (value) {
          result[key] = value
        }
      }
    })
    delete result.sort
    return result
  }

  get tableCodeToOrders (): { [tableCode: string]: IOrder[] } {
    return groupBy(this.storeOrdings, 'tableCode')
  }

  /** 手机端订单今天列表页查询条件 */
  get transformTodayMobileOrder () {
    const today: any[] = []
    const yestoday: any[] = []
    map(this.orders, order => {
      if ((order.status === 'Pending') || (ddayjs(order.addTime).format('YYYY-MM-DD') === ddayjs().format('YYYY-MM-DD'))) {
        today.push(order)
      } else {
        yestoday.push(order)
      }
    })
    return { today: today || [], history: yestoday }
  }

  /** 生成今天的状态数据查询条件 */
  get TranformTodayRequestParams () {
    return {
      ...this.transformFilterOrder,
      startAcceptTime: ddayjs().startOf('day').format(),
      endAcceptTime: ddayjs().endOf('day').format(),
      pageNo: this.OrderpageInfo.today.pageNo
    }
  }

  /** 生成昨天的状态数据查询条件 */
  get TranformYestodayRequestParams () {
    return {
      ...this.transformFilterOrder,
      startAcceptTime: ddayjs().subtract(1, 'days').startOf('day').format(),
      endAcceptTime: ddayjs().subtract(1, 'days').endOf('day').format(),
      pageNo: this.OrderpageInfo.yestoday.pageNo,
      ...this.extraQueryTime
    }
  }

  get TranformReservationsData () {
    return groupBy(this.orders, item => ddayjs(item.startTime, 'YYYY-MM-DD'))
  }

  get getSelectedSearchType () {
    return this.selectedSearchType
  }

  /** 生成外卖列表 */
  get deliveryList () {
    // 从对象内取出，根据COUNT字段进行排序
    const deliverList: any[] = Object.keys(this.devliverCountMap).map(key => {
      return {
        label: this.thirdDeliveryWay[`${key}`]?.name || `${key}`,
        value: key,
        count: this.devliverCountMap[key] || 0,
      }
    }).sort((a, b) => b.count - a.count)
    // 返回处理好的外卖列表
    return deliverList
  }

  /** 生成外卖自提列表 */
  get pickupList () {
    // 从对象内取出，根据COUNT字段进行排序
    const pickupContMap: any = {}
    each(this.storeTotalSum?.list || {}, (k, v) => {
      if (v?.includes('pickup_')) {
        const key = v.replace('pickup_', '')
        pickupContMap[key] = k
      }
    })
    const pickupList: any[] = Object.keys(pickupContMap)?.map((key) => {
      return {
        label: this.thirdDeliveryWay[`${key}`]?.name || `${key}`,
        value: key,
        count: pickupContMap[key] || 0,
      }
    })?.sort((a, b) => b.count - a.count)
    // 返回处理好的外卖列表
    return pickupList
  }

  /** 获取当前订单中心中待支付的第一笔订单 */
  async getFirstPayingOrder () {
    /** 如果用户没有登录, 则直接返回 */
    if (!restaurantConfig?.member?.id) {
      return false
    }
    try {
      const list = await this.mobilelist({ pageNo: 0, pageSize: 10 }, false, true)
      list.sort((a: any, b: any) => ddayjs(b.addTime).valueOf() - ddayjs(a.addTime).valueOf())
      if (list.length && list[0].order.status === 'Success') {
        return null
      }
      return list.find((order: any) => {
        return (order.status === 'Paying' || order.status === 'Pending') && restaurantConfig.storeId === order.storeId
      }) as IOrder
    } catch (err) {
      return null
    }
  }

  changeOrderStyle (orderStyle: TOrderStyle) {
    this.orderStyle = orderStyle
  }

  toggleYesterDay (value?: boolean) {
    if (value === undefined) {
      this.showYesterDayInfo = !this.showYesterDayInfo
    } else {
      this.showYesterDayInfo = value
    }
  }

  async setOrderPageInfo (data: 'today' | 'yestoday') {
    if (this.OrderpageInfo.status !== data) {
      this.OrderpageInfo.status = data
      return this.posRequest(this.OrderpageInfo.status, true)
    }
  }
  setExtraQueryTime = (option?: {startAcceptTime?: string; endAcceptTime?: string }) => {
    this.extraQueryTime = option
  }

  setDoFilterOrder (value: any, key: keyof IFilterOrder) {
    if (key !== 'status' || (key === 'status' && (!this.doFilterOrder.status.includes(value) || Array.isArray(value[0])))) {
      // 数组类型兼容样式二的组合状态
      this.doFilterOrder[key] = Array.isArray(value[0]) ? [...value[0]] : value
      if (restaurantConfig.app === 'pos') {
        // 每次条件发生变化时，都要重置分页数, 并且把分页id为今天的
        this.resetPageInfo()
        this.toggleYesterDay(false)
        this.posRequest('today')
      } else {
        runInAction(() => {
          this.list(this.transformFilterOrder)
        })
      }
    }
  }

  toggleSort () {
    this.doFilterOrder.sort = this.doFilterOrder.sort === 'desc' ? 'asc' : 'desc'
  }

  resetPageInfo () {
    if (restaurantConfig.app === 'pos') {
      this.OrderpageInfo = {
        status: 'today',
        today: {
          pageNo: 0,
          isEnd: false
        },
        yestoday: {
          pageNo: 0,
          isEnd: false
        }
      }
    } else {
      this.MobileOrderPageInfo = {
        pageNo: 0,
        isEnd: false
      }
    }
  }

  setDoSearchOrder (value: any, key: keyof ISearchOrderOptions) {
    this.doSearchOrder[key] = value
  }

  resetFilterOrderOptions (base = {}) {
    this.doFilterOrder = {
      status: ['open'],
      channel: 'all',
      eatType: 'all',
      sortName: 'addTime',
      sortType: '1',
      sort: 'desc',
      ...base
    }
  }

  /** 如果merge是true, 则合并，否则是覆盖 */
  async posRequest (type: 'today' | 'yestoday', merge = false, isForce = false, filterFn?: (order: any) => boolean) {
    return new Promise<void>((resolve) => {
      this.isLoading = true
      this.list(
        type === 'today'
          ? this.TranformTodayRequestParams
          : this.TranformYestodayRequestParams,
        true
      ).then((res: any) => {
        if (isEmpty(res)) {
          return resolve()
        }
        const { content = [], last, pageable, number = 0 } = res
        runInAction(() => {
          filterFn && (this.orders = this.orders.filter(filterFn))
          if (merge) {
            // 跨天查询直接合并另一天的订单
            this.orders = [...this.orders, ...content]
          } else if (number === 0 && !merge) {
            this.orders = content
          } else if (number !== 0 && !merge) {
            this.orders = [...this.orders, ...content]
          }
          this.updatePageLoad(type, { last, pageable })
          // 防止订单重复
          const uniqOrdersIds: string[] = []
          // 需要从订单列表中筛选出包含的外卖平台用作查询条件
          this.orders = this.orders.filter(order => {
            // 只处理外卖类型的订单(自营外卖单独计算)
            if (order.eatType === 'delivery' && ['3rdDelivery', 'deliverect'].includes(order.channel)) {
              // 如果是第一次遇到
              if (!this.devliverCountMap[order.sourceType]) {
                // 计数记为1
                this.devliverCountMap[order.sourceType] = 1
              } else if (this.deliveryIdCache[order.id] !== true) {
                // 否则就加一
                this.devliverCountMap[order.sourceType] += 1
              }
              this.deliveryIdCache[order.id] = true
            } else if (order.eatType === 'delivery') {
              // 单独处理自营外卖
              // 如果是第一次遇到
              if (!this.devliverCountMap[order.channel]) {
                // 计数记为1
                this.devliverCountMap[order.channel] = 1
              } else if (this.deliveryIdCache[order.id] !== true) {
                // 否则就加一
                this.devliverCountMap[order.channel] += 1
              }
              this.deliveryIdCache[order.id] = true
            }
            if (!uniqOrdersIds.includes(order.ordersId)) {
              uniqOrdersIds.push(order.ordersId)
              return order
            }
          })
          this.isLoading = false
          resolve()
        })
      })
      this.todayOrderTotalSum(isForce)
    })
  }

  async searchRequest (status?: 'add') {
    if (this.isLoading) {
      return
    }
    this.isLoading = true
    const searchParams = {
      [this.doSearchOrder.type || 'nono']: this.doSearchOrder.search,
      startAcceptTime: this.doSearchOrder.day[0],
      endAcceptTime: this.doSearchOrder.day[1],
      pageNo: this.doSearchOrder.pageNo,
    }
    try {
      const res: any = await this.list(searchParams, true).catch(err => {
        return { error: true, message: err.message }
      })
      runInAction(() => {
        const { content, last = false } = res
        this.searchOrder = status ? [...this.searchOrder, ...content] : content || []
        this.setDoSearchOrder(last, 'last')
        this.isLoading = false
      })
    } catch (err) {
      runInAction(() => {
        this.searchOrder = []
        this.isLoading = false
      })
    }
  }

  /** 初始化查询条件并关闭查询状态 */
  ressetSearchOrder () {
    this.resetSearchOrderOptions()
    this.searchOrder = []
  }

  /* 清空数据 */
  clearOrder () {
    this.searchOrder = []
    this.ordersByDate.today = []
    this.ordersByDate.yestoday = []
    this.orders = []
  }

  resetSearchOrderOptions (base = {}) {
    this.doSearchOrder = {
      search: '',
      type: 'nono',
      pageNo: 0,
      last: false,
      day: [ddayjs().subtract(3, 'month').startOf('day').format(), ddayjs().endOf('day').format()],
      ...base
    }
  }

  updatePageLoad (type: 'today' | 'yestoday', { last, pageable }: any) {
    if (last) {
      this.OrderpageInfo[type].isEnd = true
    } else {
      this.OrderpageInfo[type].pageNo = (pageable?.pageNumber || 0) + 1
    }
  }

  MobileUpdatePageLoad ({ last, pageable }: any) {
    if (last) {
      this.MobileOrderPageInfo.isEnd = true
    } else {
      this.MobileOrderPageInfo.pageNo = (pageable.pageNumber || 0) + 1
    }
  }

  /** 初始化调用  isForce为是否强制刷新 info是是否刷新数量 */
  async init (isForce: boolean = true, info?: any) {
    if (!isForce) {
      return
    }
    if (restaurantConfig.app === 'pos') {
      this.resetPageInfo()
      this.toggleYesterDay(false)
    } else {
      return this.mobilelist(info)
    }
  }
  // if isArray scarchParam; search many
  // assignment 手动赋值
  async list (searchParam: any = {}, assignment = false, pageSize?: number) {
    let searchParamObj = {
      ...searchParam
    }
    if (searchParamObj.status?.length && some(searchParamObj.status, item => [null, undefined, 'null', 'undefined'].includes(item))) {
      searchParamObj.status = ['Pending', 'Paying', 'NotAccepted', 'Success', 'Cancel', 'Timeout']
      this.partialSetDoFilterOrder('status', ['Pending', 'Paying', 'NotAccepted', 'Success', 'Cancel', 'Timeout'])
    }
    this.isLoading = true
    this.isError = false
    let params = {
      brandId: storeMobx.info.brandId || restaurantConfig.brandId,
      storeId: storeMobx.info.storeId || restaurantConfig.storeId,
      startAddTime: ddayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endAddTime: ddayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      pageSize: pageSize || 30,
      showExt: 1,
      showApplyRefund: 1,
      ...searchParamObj
    }
    if (params.startAcceptTime || params.endAcceptTime) {
      delete params.startAddTime
      delete params.endAddTime
    }
    if (params.sortType) {
      params.sortType = params.sortType - 0
    }
    const isCanCallMeal: boolean = params.status?.length === 1 && params.status[0] === 'Success' && this.isCanCallMeal
    if (isCanCallMeal) {
      params = {
        ...params,
        ...this.kdsPara
      }
    }
    return (OrderAPI.list(params, isCanCallMeal)).then((res: any) => {
      if (assignment) {
        runInAction(() => {
          this.isLoading = false
          this.isError = false
        })
        return res.result || {
          content: [],
          last: true,
          pageable: true,
          number: 0
        }
      } else {
        runInAction(() => {
          this.orders = res.result ? res.result?.content : []
          this.isLoading = false
          this.isError = false
        })
      }
    }, (err: any) => {
      runInAction(() => {
        this.isLoading = false
        this.isError = true
      })
      return err?.message || ''
    }).catch(() => {
      this.isLoading = false
      this.isError = true
    })
  }

  updateOrder (index: number, order: IOrder) {
    runInAction(() => {
      this.orders[index] = order
    })
  }

  setIsCanCallMealVal (val: boolean) {
    runInAction(() => {
      this.isCanCallMeal = val
    })
  }

  setKdsPara (val: any) {
    runInAction(() => {
      this.kdsPara = {
        ...this.kdsPara,
        ...val
      }
    })
  }

  /** 2021-1-9 search time add new Date */
  mobilelist (searchParam: any = {}, merge = false, assignment = false) {
    const timer = new Date().valueOf()
    return OrderAPI.memberlist({
      brandId: storeMobx.info.brandId,
      startAddTime: ddayjs(timer).subtract(30, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endAddTime: ddayjs(timer).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      pageSize: 100,
      pageNo: this.MobileOrderPageInfo.pageNo,
      ...searchParam
    }).then((res: any) => {
      if (res.result) {
        const { last, pageable } = res.result
        if (assignment) {
          return res.result ? res.result.content : []
        }
        runInAction(() => {
          if (merge) {
            let result = res.result ? res.result.content : []
            result = result.sort((a: any, b: any) => new Date(b.addTime).getTime() - new Date(a.addTime).getTime())
            this.orders = [...this.orders, ...result].filter(order => order.nono)
          } else {
            let result = res.result ? res.result.content : []
            this.orders = result.filter((order: any) => order.nono).sort((a: any, b: any) => new Date(b.addTime).getTime() - new Date(a.addTime).getTime())
          }
          this.MobileUpdatePageLoad({ last, pageable })
        })
      }
    })
  }

  /** 初始化调用 */
  async Reservationsinit () {
    if (restaurantConfig.app !== 'pos') {
      return
    }
    return (OrderAPI as any).reservationlist({
      brandId: storeMobx.info.brandId || restaurantConfig.brandId,
      storeId: storeMobx.info.storeId || restaurantConfig.storeId,
      startStartTime: ddayjs().startOf('day').format(),
      endStartTime: ddayjs().add(15, 'days').endOf('day').format(),
    }).then((res: any) => {
      runInAction(() => {
        this.reservationOrder = res?.result || []
      })
    })
  }

  async ResetOrders () {
    this.orders = []
  }

  async ResetReservationOrder () {
    this.reservationOrder = []
  }

  todayOrderTotalSum (isForce: boolean = false) {
    if (Date.now() - this.storeTotalSum.parentTime > 5000 || isForce) {
      return OrderAPI.statistics({
        brandId: storeMobx.info.brandId || restaurantConfig.brandId,
        storeId: storeMobx.info.storeId || restaurantConfig.storeId,
        startAcceptTime: ddayjs().startOf('day').format(),
        endAcceptTime: ddayjs().endOf('day').format(),
      }).then((res: any) => {
        runInAction(() => {
          const { cancelCnt, pendingCnt, successCnt, timeoutCnt, notAcceptCnt, eatTypeCnt } = res?.result || {}
          this.storeTotalSum = {
            parentTime: Date.now(),
            list: {
              ...eatTypeCnt,
              open: (pendingCnt || 0) + (notAcceptCnt || 0),
              paid: successCnt || 0,
              voided: cancelCnt || 0,
              timeout: timeoutCnt || 0,
              all: (pendingCnt || 0) + (notAcceptCnt || 0) + (cancelCnt || 0) + (successCnt || 0) + (timeoutCnt || 0),
            }
          }
          const eatTypeCntKeys = Object.keys(eatTypeCnt || {})
          if (eatTypeCntKeys.includes('dinein')) {
            this.storeTotalSum.list = {
              ...this.storeTotalSum.list,
              dinein: ((eatTypeCnt && eatTypeCnt.dinein) || 0),
              dineinPos: ((eatTypeCnt && eatTypeCnt.dinein_pos) || 0)
            }
          }
          if (eatTypeCntKeys.includes('togo')) {
            this.storeTotalSum.list = {
              ...this.storeTotalSum.list,
              togo: ((eatTypeCnt && eatTypeCnt.togo) || 0),
              togoPos: ((eatTypeCnt && eatTypeCnt.togo_pos) || 0)
            }
          }
          if (eatTypeCntKeys.includes('delivery') || eatTypeCntKeys.includes('pickup') || eatTypeCntKeys.includes('carside')) {
            eatTypeCntKeys.forEach(element => {
              this.storeTotalSum.list = {
                ...this.storeTotalSum.list,
                [element]: eatTypeCnt[element]
              }
            })
          }
        })
      })
    }
  }

  /** 根据桌台号返回当前正在进行中的订单 */
  async getTabelCodeOrder (tableCode: string | string[]) {
    if (!tableCode) {
      Promise.reject(null)
    } else {
      const obj = {
        // tableCode: isArray(tableCode) ? tableCode : [tableCode], 2020-12-29 tableCode变成数字
        tableCode,
        status: ['Pending', 'NotAccepted', 'Paying'],
        startAddTime: ddayjs().subtract(3, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      }

      // 桌台订单
      const orders = await (DeskAPI as any).getTableByTableCode({
        storeId: storeMobx.info.storeId || restaurantConfig.storeId,
        tableCode: String(tableCode),
      })
      const { ordersId } = orders?.find((item: any) => item.ordersId) || {}
      return this.list(obj, true).then((res: any) => (res?.content?.filter((o: any) => o.ordersId === ordersId) || []))
    }
  }

  /** 获取当前订单中心中桌台订单的第一笔订单 */
  async getFirstTabelCodeOrder (tableCode: string | string[]) {
    return this.getTabelCodeOrder(tableCode).then((orderList: any) => {
      if (orderList && orderList.length) {
        return orderList[0]
      } else {
        throw orderList
      }
    })
  }

  /** 当前门店正在进行中的订单 */
  async storeOrdingsInit () {
    deskMobx.getTableStatusList()
    return this.list({
      status: ['Pending', 'NotAccepted', 'Paying'],
      startAddTime: ddayjs().subtract(3, 'days').startOf('day').format(),
      showExt: 1
    }, true, 300)
      .then((res: any) => (res?.content || []))
      .then(res => {
        runInAction(() => {
          this.storeOrdings = res
        })
      })
  }

  /** 获取当前会员需要录桌的订单 */
  async memberNeedTablelist (memberId?: number) {
    if (!memberId) {
      return []
    }
    return (OrderAPI as any).memberNeedTablelist({
      memberId: String(memberId)
    }).then((res: any) => {
      const content = res?.result?.content || []
      runInAction(() => {
        this.needTableList = content
      })
      return content
    })
  }

  /**
   * 搜索条件为全部时，重置两个入参
   * @param key 对应的键
   * @param value 更新的值
   */
  partialSetDoFilterOrder (key: keyof IFilterOrder, value: any) {
    this.doFilterOrder[key] = value
  }

  /** 更新选中的类型 */
  setSelectedSearchType (type: string) {
    this.selectedSearchType = type
  }

  /** 更新选中的搜索项 */
  setSelectedOptions (options: string[]) {
    this.selectedOptions = options
  }

  /** 新搜索框下的接口查询 */
  setDoFilterOrderForNewModal (filterInfo: any) {
    const filterData = { sourceType: [], channel: [], ...filterInfo }
    for (const key in filterData) {
      this.partialSetDoFilterOrder(key as keyof IFilterOrder, filterData[key])
    }
    if (restaurantConfig.app === 'pos') {
      // 每次条件发生变化时，都要重置分页数, 并且把分页id为今天的
      this.resetPageInfo()
      this.toggleYesterDay(false)
      this.posRequest('today')
    } else {
      runInAction(() => {
        this.list(this.transformFilterOrder)
      })
    }
  }

  /**
   * 局部更新订单
   */
  partialRefreshOrderList (orderId: string, orderMapping: (order: IOrder) => IOrder) {
    this.orders = this.orders.map(order => {
      if (order.ordersId === orderId) {
        return orderMapping(order)
      }
      return {
        ...order
      }
    })
  }

  /**
   * 第三方外卖方式
   * @param ways any
   */
  setThirdDeliveryWay (ways: any) {
    this.thirdDeliveryWay = ways
  }

  async callRider (ordersId: string, storeId: number, facanStauts?: IOutOrderStatus) {
    const data = await OrderAPI.callRider({ ordersId, storeId, facanStauts })
    return data
  }
}

export default new OrderList()
